import React, { useState, useEffect, useMemo } from 'react'
import {
  CalendarIcon,
  UserGroupIcon,
  TrashIcon,
  XMarkIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { apiService } from '../services/api'
import {
  getDateRange,
  getScheduleForDay,
  buildTimeSlots,
  getSlotDateTime,
  isDateClosed,
  isSlotTooSoon,
  type DaySchedule
} from '../utils/reservationSlots'

interface Table {
  id: string
  number: string
  capacity: number
  location: 'indoor' | 'outdoor' | 'bar'
}

interface Reservation {
  id: string
  table_id: string
  customer_name: string
  customer_phone: string
  customer_contact?: string
  customer_email?: string
  party_size: number
  reservation_time: string
  status: 'pending' | 'confirmed' | 'seated' | 'cancelled' | 'no-show' | 'rejected'
  notes?: string
  created_at: string
  updated_at?: string
  tables?: Table
}

const Reservations: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null)
  const [showReservationModal, setShowReservationModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [reservationToDelete, setReservationToDelete] = useState<Reservation | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'upcoming' | 'past'>('all')
  const [isSaving, setIsSaving] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [tables, setTables] = useState<Table[]>([])
  const [restaurantSchedule, setRestaurantSchedule] = useState<Record<string, DaySchedule> | undefined>(undefined)
  const [restaurantHolidays, setRestaurantHolidays] = useState<string[]>([])
  const [slotDurationMinutes, setSlotDurationMinutes] = useState(30)
  const [minimumBookingHours, setMinimumBookingHours] = useState(2)

  useEffect(() => {
    loadReservations()
    loadTables()
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiService.getRestaurantSettings()
        if (res.success && res.data) {
          const d = res.data as any
          setRestaurantSchedule(d.restaurant_schedule ?? undefined)
          setRestaurantHolidays(Array.isArray(d.restaurant_holidays) ? d.restaurant_holidays : [])
          setSlotDurationMinutes(Math.max(15, Number(d.reservation_timeslot_duration_minutes) || 30))
          setMinimumBookingHours(Math.max(0, Number(d.reservation_minimum_booking_hours)) || 2)
        }
      } catch (e) {
        console.error('Failed to load restaurant settings for reservations', e)
      }
    }
    load()
  }, [])

  const loadTables = async () => {
    try {
      const response = await apiService.getTables()
      if (response.success) {
        setTables(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading tables:', error)
    }
  }

  const loadReservations = async () => {
    try {
      setLoading(true)
      const response = await apiService.getReservations()
      if (response.success) {
        // Map customer_contact to customer_phone for frontend compatibility
        const transformedData = (Array.isArray(response.data) ? response.data : []).map((reservation: any) => ({
          ...reservation,
          customer_phone: reservation.customer_phone || reservation.customer_contact,
          // Ensure table_id is set even if tables is nested
          table_id: reservation.table_id || reservation.tables?.id || ''
        }))
        setReservations(transformedData)
      } else {
        setError('Failed to load reservations')
      }
    } catch (error) {
      console.error('Error loading reservations:', error)
      setError('Failed to load reservations')
    } finally {
      setLoading(false)
    }
  }

  const filteredReservations = useMemo(() => {
    let filtered = reservations

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(reservation =>
        reservation.customer_name.toLowerCase().includes(searchLower) ||
        (reservation.customer_phone || reservation.customer_contact || '').toLowerCase().includes(searchLower) ||
        reservation.tables?.number.toLowerCase().includes(searchLower)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(reservation => reservation.status === statusFilter)
    }

    // Date filter
    const now = new Date()
    if (dateFilter === 'today') {
      filtered = filtered.filter(reservation => {
        const resDate = new Date(reservation.reservation_time)
        return resDate.toDateString() === now.toDateString()
      })
    } else if (dateFilter === 'upcoming') {
      filtered = filtered.filter(reservation => {
        const resDate = new Date(reservation.reservation_time)
        return resDate >= now && reservation.status !== 'cancelled' && reservation.status !== 'no-show'
      })
    } else if (dateFilter === 'past') {
      filtered = filtered.filter(reservation => {
        const resDate = new Date(reservation.reservation_time)
        return resDate < now || reservation.status === 'cancelled' || reservation.status === 'no-show'
      })
    }

    // Sort by reservation time (upcoming first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.reservation_time).getTime()
      const dateB = new Date(b.reservation_time).getTime()
      return dateA - dateB
    })
  }, [reservations, searchTerm, statusFilter, dateFilter])

  // Get available tables based on selected reservation time
  const getAvailableTables = useMemo(() => {
    if (!selectedReservation?.reservation_time) {
      return tables
    }

    const selectedTime = new Date(selectedReservation.reservation_time)
    const oneHourBefore = new Date(selectedTime.getTime() - 60 * 60 * 1000) // 1 hour before
    const oneHourAfter = new Date(selectedTime.getTime() + 60 * 60 * 1000) // 1 hour after

    return tables.filter(table => {
      // When editing, always include the currently selected table
      const currentTableId = selectedReservation.table_id || (selectedReservation.tables as any)?.id
      if (selectedReservation.id && table.id === currentTableId) {
        return true
      }

      // Check if this table has any conflicting reservations
      const hasConflict = reservations.some(reservation => {
        // Skip cancelled and no-show reservations
        if (reservation.status === 'cancelled' || reservation.status === 'no-show') {
          return false
        }

        // When editing, exclude the current reservation from conflict check
        if (selectedReservation.id && reservation.id === selectedReservation.id) {
          return false
        }

        // Check if reservation is for this table
        const reservationTableId = reservation.table_id || (reservation.tables as any)?.id
        if (reservationTableId !== table.id) {
          return false
        }

        // Check if reservation time conflicts (within 1 hour)
        const reservationTime = new Date(reservation.reservation_time)
        return reservationTime >= oneHourBefore && reservationTime <= oneHourAfter
      })

      return !hasConflict
    })
  }, [tables, reservations, selectedReservation?.reservation_time, selectedReservation?.id, selectedReservation?.table_id])

  const handleEdit = (reservation: Reservation) => {
    const editReservation: Reservation = {
      ...reservation,
      customer_phone: reservation.customer_phone || reservation.customer_contact || '',
      customer_contact: reservation.customer_contact || reservation.customer_phone || '',
      customer_email: reservation.customer_email || '',
      table_id: reservation.table_id || (reservation.tables as any)?.id || ''
    }
    setSelectedReservation(editReservation)
    setShowReservationModal(true)
    setError(null)
  }

  const handleDelete = (reservation: Reservation) => {
    setReservationToDelete(reservation)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!reservationToDelete) return

    try {
      const response = await apiService.deleteReservation(reservationToDelete.id)
      if (response.success) {
        setSuccessMessage('Reservation deleted successfully')
        setShowSuccessModal(true)
        await loadReservations()
      } else {
        setError(response.error || 'Failed to delete reservation')
      }
    } catch (error) {
      console.error('Error deleting reservation:', error)
      setError('Failed to delete reservation')
    } finally {
      setShowDeleteModal(false)
      setReservationToDelete(null)
    }
  }

  const handleSave = async () => {
    if (!selectedReservation) return

    try {
      setIsSaving(true)
      setError(null)

      const reservationData = {
        customer_name: selectedReservation.customer_name,
        customer_phone: selectedReservation.customer_phone || selectedReservation.customer_contact || '',
        customer_email: selectedReservation.customer_email || '',
        party_size: selectedReservation.party_size,
        reservation_time: selectedReservation.reservation_time,
        status: selectedReservation.status,
        notes: selectedReservation.notes || '',
        table_id: selectedReservation.table_id || (selectedReservation.tables as any)?.id || ''
      }

      let response
      if (selectedReservation.id && selectedReservation.id !== '') {
        // Update existing reservation
        response = await apiService.updateReservation(selectedReservation.id, reservationData)
        if (response.success) {
          setSuccessMessage('Reservation updated successfully')
        }
      } else {
        // Create new reservation
        response = await apiService.createReservation(reservationData)
        if (response.success) {
          setSuccessMessage('Reservation created successfully')
        }
      }

      if (response.success) {
        setShowSuccessModal(true)
        setShowReservationModal(false)
        setSelectedReservation(null)
        await loadReservations()
      } else {
        setError(response.error || `Failed to ${selectedReservation.id ? 'update' : 'create'} reservation`)
      }
    } catch (error) {
      console.error(`Error ${selectedReservation.id ? 'updating' : 'creating'} reservation:`, error)
      setError(`Failed to ${selectedReservation.id ? 'update' : 'create'} reservation`)
    } finally {
      setIsSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'seated':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'cancelled':
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'no-show':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const buildReservationPayload = (r: Reservation, status?: string) => {
    const tableId = r.table_id || (r.tables as any)?.id
    return {
      customer_name: r.customer_name,
      customer_phone: r.customer_phone || r.customer_contact || '',
      customer_email: r.customer_email || '',
      party_size: r.party_size,
      reservation_time: r.reservation_time,
      status: status ?? r.status,
      notes: r.notes || '',
      table_id: tableId || null
    }
  }

  const handleApprove = async (reservation: Reservation) => {
    try {
      setError(null)
      const response = await apiService.updateReservation(reservation.id, buildReservationPayload(reservation, 'confirmed'))
      if (response.success) {
        setSuccessMessage('Reservation approved')
        setShowSuccessModal(true)
        await loadReservations()
      } else {
        setError(response.error || 'Failed to approve')
      }
    } catch (err) {
      console.error('Approve reservation error:', err)
      setError('Failed to approve reservation')
    }
  }

  const handleReject = async (reservation: Reservation) => {
    try {
      setError(null)
      const response = await apiService.updateReservation(reservation.id, buildReservationPayload(reservation, 'rejected'))
      if (response.success) {
        setSuccessMessage('Reservation rejected')
        setShowSuccessModal(true)
        await loadReservations()
      } else {
        setError(response.error || 'Failed to reject')
      }
    } catch (err) {
      console.error('Reject reservation error:', err)
      setError('Failed to reject reservation')
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const dateRange = useMemo(() => getDateRange(), [])
  const reservationDate = selectedReservation?.reservation_time
    ? (() => {
        const d = new Date(selectedReservation.reservation_time)
        return new Date(d.getFullYear(), d.getMonth(), d.getDate())
      })()
    : null
  const reservationTimeSlot = selectedReservation?.reservation_time
    ? (() => {
        const d = new Date(selectedReservation.reservation_time)
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
      })()
    : ''
  const selectedDaySchedule = reservationDate ? getScheduleForDay(restaurantSchedule, reservationDate.getDay()) : null
  const selectedDateClosed = reservationDate ? isDateClosed(reservationDate, restaurantSchedule, restaurantHolidays) : true
  const timeSlotsForSelectedDate =
    reservationDate && !selectedDateClosed && selectedDaySchedule
      ? buildTimeSlots(slotDurationMinutes, selectedDaySchedule.open, selectedDaySchedule.close)
      : []
  const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  const setReservationDateAndTime = (date: Date, slot: string) => {
    if (!selectedReservation) return
    const daySchedule = getScheduleForDay(restaurantSchedule, date.getDay())
    const iso = getSlotDateTime(date, slot, daySchedule).toISOString()
    setSelectedReservation({ ...selectedReservation, reservation_time: iso })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Reservations</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage all restaurant reservations
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedReservation({
              id: '',
              table_id: '',
              customer_name: '',
              customer_phone: '',
              customer_email: '',
              party_size: 1,
              reservation_time: new Date().toISOString(),
              status: 'confirmed',
              notes: '',
              created_at: new Date().toISOString()
            } as Reservation)
            setShowReservationModal(true)
            setError(null)
          }}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Reservation
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search reservations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="seated">Seated</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
            <option value="no-show">No Show</option>
          </select>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>

          {/* Clear Filters */}
          {(searchTerm || statusFilter !== 'all' || dateFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('')
                setStatusFilter('all')
                setDateFilter('all')
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Reservations List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {filteredReservations.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No reservations found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating a new reservation'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Table
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Party Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reservation Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredReservations.map((reservation) => (
                  <tr key={reservation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {reservation.customer_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {reservation.customer_phone || reservation.customer_contact || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {reservation.customer_email || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {reservation.tables?.number || 'N/A'}
                        {reservation.tables?.location && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                            ({reservation.tables.location})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {reservation.party_size}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {formatDateTime(reservation.reservation_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(reservation.status)}`}>
                        {reservation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(reservation)}
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          title="View details"
                        >
                          <EyeIcon className="h-5 w-5" />
                          <span>View details</span>
                        </button>
                        <button
                          onClick={() => handleDelete(reservation)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit/Create Reservation Modal */}
      {showReservationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {selectedReservation?.id && selectedReservation.id !== '' ? 'Reservation details' : 'New Reservation'}
              </h2>
              <button
                onClick={() => {
                  setShowReservationModal(false)
                  setSelectedReservation(null)
                  setError(null)
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Step 1: Date and Time (business hours, slot duration, holidays - same as customer flow) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date *
                </label>
                <div className="flex flex-wrap gap-2">
                  {dateRange.map((d) => {
                    const closed = isDateClosed(d, restaurantSchedule, restaurantHolidays)
                    const isSelected = reservationDate && reservationDate.getTime() === d.getTime()
                    return (
                      <button
                        key={d.getTime()}
                        type="button"
                        onClick={() => {
                          if (closed) return
                          const daySched = getScheduleForDay(restaurantSchedule, d.getDay())
                          const slots = buildTimeSlots(slotDurationMinutes, daySched.open, daySched.close)
                          const firstAvailable = slots.find((slot) => !isSlotTooSoon(getSlotDateTime(d, slot, daySched), minimumBookingHours)) || slots[0] || '16:00'
                          setReservationDateAndTime(d, firstAvailable)
                        }}
                        disabled={closed}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                          closed
                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                            : isSelected
                              ? 'bg-primary-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-primary-100 dark:hover:bg-primary-900'
                        }`}
                      >
                        {DAY_NAMES[d.getDay()]}, {d.getDate()} {d.toLocaleDateString(undefined, { month: 'short' })}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Time *
                </label>
                {!reservationDate ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Select a date first.</p>
                ) : selectedDateClosed ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Closed on this day.</p>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {(timeSlotsForSelectedDate.includes(reservationTimeSlot) ? timeSlotsForSelectedDate : [...timeSlotsForSelectedDate, reservationTimeSlot].filter(Boolean)).map((slot) => {
                      const slotDt = selectedDaySchedule ? getSlotDateTime(reservationDate!, slot, selectedDaySchedule) : null
                      const tooSoon = slotDt ? isSlotTooSoon(slotDt, minimumBookingHours) : false
                      const isSelected = reservationTimeSlot === slot
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => !tooSoon && setReservationDateAndTime(reservationDate!, slot)}
                          disabled={tooSoon}
                          className={`py-2 rounded-lg text-sm font-medium ${
                            tooSoon
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                              : isSelected
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 hover:bg-primary-100 dark:hover:bg-primary-900'
                          }`}
                          title={tooSoon ? `Book at least ${minimumBookingHours} hour(s) ahead` : undefined}
                        >
                          {slot}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Step 2: Table Selection (only shown after date/time is selected) */}
              {selectedReservation?.reservation_time && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Table *
                  </label>
                  {getAvailableTables.length === 0 ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                      <p className="text-sm text-red-800 dark:text-red-200">
                        No tables available for this time slot. All tables are booked within 1 hour of this time.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {getAvailableTables.map(table => {
                        const isSelected = selectedReservation?.table_id === table.id || (selectedReservation?.tables as any)?.id === table.id
                        return (
                          <button
                            key={table.id}
                            type="button"
                            onClick={() => setSelectedReservation({ ...selectedReservation!, table_id: table.id })}
                            className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                              isSelected
                                ? 'bg-primary-100 dark:bg-primary-900 border-primary-500 shadow-lg'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {table.number}
                              </h3>
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <UserGroupIcon className="h-4 w-4 mr-1" />
                                {table.capacity} seats
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-500 capitalize">
                                {table.location || 'No location'}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="mt-2 flex items-center text-primary-600 dark:text-primary-400">
                                <CheckIcon className="h-5 w-5" />
                                <span className="ml-1 text-sm font-medium">Selected</span>
                              </div>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Customer Information */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Customer Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={selectedReservation?.customer_name || ''}
                      onChange={(e) => setSelectedReservation({ ...selectedReservation!, customer_name: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Customer Phone *
                    </label>
                    <input
                      type="tel"
                      value={selectedReservation?.customer_phone || selectedReservation?.customer_contact || ''}
                      onChange={(e) => setSelectedReservation({ ...selectedReservation!, customer_phone: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={selectedReservation?.customer_email || ''}
                      onChange={(e) => setSelectedReservation({ ...selectedReservation!, customer_email: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="customer@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Party Size *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={selectedReservation?.party_size || ''}
                      onChange={(e) => setSelectedReservation({ ...selectedReservation!, party_size: parseInt(e.target.value) || 1 })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status *
                </label>
                <select
                  value={selectedReservation?.status || 'confirmed'}
                  onChange={(e) => setSelectedReservation({ ...selectedReservation!, status: e.target.value as any })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="seated">Seated</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rejected">Rejected</option>
                  <option value="no-show">No Show</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={selectedReservation?.notes || ''}
                  onChange={(e) => setSelectedReservation({ ...selectedReservation!, notes: e.target.value })}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Special requests, dietary restrictions, etc."
                />
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3 pt-4">
              <button
                onClick={() => {
                  setShowReservationModal(false)
                  setSelectedReservation(null)
                  setError(null)
                }}
                className="flex-1 min-w-[100px] bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              {selectedReservation?.id && selectedReservation.id !== '' && selectedReservation?.status === 'pending' && (
                <>
                  <button
                    onClick={async () => {
                      await handleReject(selectedReservation!)
                      setShowReservationModal(false)
                      setSelectedReservation(null)
                      setError(null)
                    }}
                    className="flex-1 min-w-[100px] bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Reject
                  </button>
                  <button
                    onClick={async () => {
                      await handleApprove(selectedReservation!)
                      setShowReservationModal(false)
                      setSelectedReservation(null)
                      setError(null)
                    }}
                    className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Approve
                  </button>
                </>
              )}
              <button
                onClick={handleSave}
                disabled={isSaving || !selectedReservation?.customer_name || !selectedReservation?.customer_phone || !selectedReservation?.reservation_time}
                className="flex-1 min-w-[100px] bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {isSaving ? 'Saving...' : selectedReservation?.id && selectedReservation.id !== '' ? 'Save changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && reservationToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Delete Reservation
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the reservation for{' '}
              <strong>{reservationToDelete.customer_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setReservationToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
              Success!
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              {successMessage}
            </p>
            <button
              onClick={() => {
                setShowSuccessModal(false)
                setSuccessMessage('')
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reservations

